import { API } from './site';

const validationMessage = (field, message) => ({
  phone: 'Enter a phone number with 7–15 digits, including the area code.',
  email: 'Enter a valid email address, such as you@example.com.',
  name: 'Please keep your name to 100 characters or fewer.',
  consent: 'Please agree to be contacted before saving your details.',
}[field] || message?.replace(/^Value error,\s*/i, '') || 'Please check this field.');

export const callRequest = async (path, body, options = {}) => {
  const response = await fetch(`${API}${path}`, { method: body ? 'POST' : 'GET', headers: { 'Content-Type': 'application/json' }, ...(body ? { body: JSON.stringify(body) } : {}), signal: AbortSignal.timeout(20000), ...options });
  const data = await response.json();
  if (!response.ok) {
    const fieldErrors = {};
    if (Array.isArray(data.detail)) for (const item of data.detail) {
      const field = item.loc?.[item.loc.length - 1];
      if (typeof field === 'string') fieldErrors[field] = validationMessage(field, item.msg);
    }
    const error = new Error(typeof data.detail === 'string' ? data.detail : Object.keys(fieldErrors).length ? 'Please check the highlighted fields and try again.' : 'Unable to save this call. Please try again.');
    error.fieldErrors = fieldErrors; error.status = response.status;
    throw error;
  }
  return data;
};

export const streamReply = async (body, signal, onDelta, onDone, onIntake, onMeta) => {
  const response = await fetch(`${API}/chat`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), signal });
  if (!response.ok) {
    const error = await response.json();
    throw new Error(typeof error.detail === 'string' ? error.detail : 'The AI receptionist is unavailable. Please try again.');
  }
  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = ''; let complete = false;
  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let boundary;
    while ((boundary = buffer.indexOf('\n\n')) !== -1) {
      const packet = buffer.slice(0, boundary); buffer = buffer.slice(boundary + 2);
      if (!packet.startsWith('data: ')) continue;
      const event = JSON.parse(packet.slice(6));
      if (event.type === 'error') throw new Error(event.text);
      if (event.type === 'done') { complete = true; onDone?.(event); }
      if (event.type === 'reply_done') onDone?.(event);
      if (event.type === 'intake') onIntake?.(event);
      if (event.type === 'language' || event.type === 'urgent') onMeta?.(event);
      if (event.type === 'delta') onDelta(event.text);
    }
  }
  if (!complete) throw new Error('The connection was interrupted. Please try again.');
};

export const takeSpeechChunk = (buffer, flush = false, first = false) => {
  if (flush) return [buffer, ''];
  for (const match of buffer.matchAll(/[.!?](?:\s|$)/g)) {
    const end = match.index + 1;
    const prefix = buffer.slice(0, end);
    const word = prefix.match(/\S+$/)?.[0] || '';
    if (/(?:\b(?:Mr|Mrs|Ms|Dr|Prof|St|vs|e\.g|i\.e|[A-Z])\.)$/i.test(word)) continue;
    // A streamed token ending in a dot is not necessarily the end of an email, URL or decimal.
    if (end === buffer.length && (word.includes('@') || /^https?:/i.test(word) || /\d\.$/.test(word))) continue;
    return [prefix, buffer.slice(match.index + match[0].length)];
  }
  const protectedPhrase = /[@\d]|\b(phone|email|number|zero|one|two|three|four|five|six|seven|eight|nine|oh|dot|at|double|triple)\b/i.test(buffer);
  if (first && !protectedPhrase) {
    const clause = buffer.match(/^([\s\S]{24,100}?[,;—])\s/);
    if (clause) return [clause[1], buffer.slice(clause[0].length)];
  }
  if (buffer.length > (protectedPhrase ? 500 : 180)) {
    const split = buffer.lastIndexOf(' ', protectedPhrase ? 480 : 165);
    if (split > 0) return [buffer.slice(0, split), buffer.slice(split + 1)];
  }
  return ['', buffer];
};